import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  DEBUG_LAST_ACTION_AT_KEY,
  DEBUG_LAST_ACTION_KEY,
  readSelectionTimestamp,
} from "../../utils/debugState";

const readLocalValue = (key) => {
  try {
    return localStorage.getItem(key) || "";
  } catch (error) {
    return "";
  }
};

const getGuardReason = ({ route, hasClient, hasProperty, hasAnalysis }) => {
  if (!route.startsWith("/dashboard")) return "—";
  if (!hasClient) return "Blocked: missing client selection";
  if (
    route === "/dashboard/layout" ||
    route === "/dashboard/directions" ||
    route === "/dashboard/analysis"
  ) {
    if (!hasProperty) return "Blocked: missing property selection";
  }
  if (route === "/dashboard/reports" && !hasAnalysis) {
    return "Blocked: missing analysis selection";
  }
  return "—";
};

export default function DebugSnapshotPanel({ enabled }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState(() => ({
    clientId: "",
    clientName: "",
    propertyId: "",
    propertyName: "",
    analysisId: "",
    route: "",
    lastAction: "",
    lastActionAt: "",
    hasClient: false,
    hasProperty: false,
    hasAnalysis: false,
    clientUpdatedAt: "",
    propertyUpdatedAt: "",
    analysisUpdatedAt: "",
    guardReason: "—",
  }));

  const refreshSnapshot = useCallback(() => {
    const clientId = readLocalValue("myv_selected_client_id");
    const clientName = readLocalValue("myv_selected_client");
    const propertyId = readLocalValue("myv_selected_property_id");
    const propertyName = readLocalValue("myv_selected_property");
    const analysisId = readLocalValue("myv_selected_analysis_id");
    const lastAction = readLocalValue(DEBUG_LAST_ACTION_KEY);
    const lastActionAt = readLocalValue(DEBUG_LAST_ACTION_AT_KEY);
    const hasClient = Boolean(clientId);
    const hasProperty = Boolean(propertyId);
    const hasAnalysis = Boolean(analysisId);
    const route = location.pathname;

    setSnapshot({
      clientId,
      clientName,
      propertyId,
      propertyName,
      analysisId,
      route,
      lastAction,
      lastActionAt,
      hasClient,
      hasProperty,
      hasAnalysis,
      clientUpdatedAt: readSelectionTimestamp("client"),
      propertyUpdatedAt: readSelectionTimestamp("property"),
      analysisUpdatedAt: readSelectionTimestamp("analysis"),
      guardReason: getGuardReason({
        route,
        hasClient,
        hasProperty,
        hasAnalysis,
      }),
    });
  }, [location.pathname]);

  useEffect(() => {
    if (!enabled) return;
    refreshSnapshot();
  }, [enabled, refreshSnapshot]);

  useEffect(() => {
    if (!enabled) return;
    const handleUpdate = () => refreshSnapshot();
    window.addEventListener("myv_debug_state_updated", handleUpdate);
    window.addEventListener("myv_selection_updated", handleUpdate);
    return () => {
      window.removeEventListener("myv_debug_state_updated", handleUpdate);
      window.removeEventListener("myv_selection_updated", handleUpdate);
    };
  }, [enabled, refreshSnapshot]);

  if (!enabled) return null;

  return (
    <div className="fixed bottom-4 right-4 z-40 w-full max-w-xs">
      <button
        type="button"
        className="btn btn-secondary w-full"
        onClick={() => setOpen((prev) => !prev)}
      >
        {open ? "Hide debug" : "Show debug"}
      </button>
      {open && (
        <div className="card mt-2 p-4 text-xs text-slate-600">
          <p className="text-sm font-semibold text-slate-900">UI Snapshot</p>
          <div className="mt-3 space-y-2">
            <div>Route: {snapshot.route || "—"}</div>
            <div>Client: {snapshot.clientName || "—"} ({snapshot.clientId || "—"})</div>
            <div>
              Property: {snapshot.propertyName || "—"} ({snapshot.propertyId || "—"})
            </div>
            <div>Analysis ID: {snapshot.analysisId || "—"}</div>
            <div>hasClient: {snapshot.hasClient ? "true" : "false"}</div>
            <div>hasProperty: {snapshot.hasProperty ? "true" : "false"}</div>
            <div>hasAnalysis: {snapshot.hasAnalysis ? "true" : "false"}</div>
            <div>Guard reason: {snapshot.guardReason}</div>
            <div>
              Last action: {snapshot.lastAction || "—"}
              {snapshot.lastActionAt ? ` @ ${snapshot.lastActionAt}` : ""}
            </div>
            <div>Client updated: {snapshot.clientUpdatedAt || "—"}</div>
            <div>Property updated: {snapshot.propertyUpdatedAt || "—"}</div>
            <div>Analysis updated: {snapshot.analysisUpdatedAt || "—"}</div>
          </div>
        </div>
      )}
    </div>
  );
}
